/**
 * The home's decisions, pure (no React, no network) so the unit tests pin them: what each tool tile's badge says, which competitions,
 * tokens and locks the live board shows, and how a time left reads. Every number on a badge comes from a read made by the caller
 * (the lockers, the V4 contract or its API, the token-pages settings, the swap's own DEX list); nothing here is a price constant.
 */
import { formatEther } from 'viem'
import type { CompetitionRow } from 'views/TradingCompetitionV3/components/CompetitionCard'
import type { LockPhase, LockSummary, Totals } from 'views/LockTools/types'
import { isFree } from 'views/TokenPages/fees'
import type { ListItem } from 'views/TokenPages/types'

/** What a tile's badge shows: a skeleton while its read is in flight, "Free", a price, a fact, or nothing (a failed read never shows a number). */
export type Badge = { kind: 'loading' } | { kind: 'free' } | { kind: 'price'; text: string } | { kind: 'info'; text: string } | { kind: 'none' }

export const bnbText = (wei: bigint) => `${formatEther(wei)} BNB`

/** One locker's fee read (views/LockTools/fees.ts useLockFees). */
export type LockFeeRead = { native?: bigint; cbon?: bigint; loading: boolean; failed: boolean }

/**
 * The Lock tile: "Free" only when every locker reads 0 in BNB and in CBON (the rule the lock form uses for its own "free" line),
 * a BNB price when one charges, nothing when a read failed (a partial answer cannot prove "free").
 */
export function lockBadge(reads: LockFeeRead[]): Badge {
  if (reads.length === 0) return { kind: 'none' }
  if (reads.some((r) => r.loading)) return { kind: 'loading' }
  if (reads.some((r) => r.failed || r.native === undefined || r.cbon === undefined)) return { kind: 'none' }
  const natives = reads.map((r) => r.native as bigint)
  if (reads.every((r) => r.native === 0n && r.cbon === 0n)) return { kind: 'free' }
  const min = natives.reduce((a, b) => (b < a ? b : a))
  const max = natives.reduce((a, b) => (b > a ? b : a))
  if (min === max) return { kind: 'price', text: bnbText(min) }
  if (min === 0n) return { kind: 'price', text: `Free or ${bnbText(max)}` }
  return { kind: 'price', text: `From ${bnbText(min)}` }
}

/** V4 feeConfig(0) as the contract answers it: [cost, costWithReferral, enabled]. */
export type ChainFee = { cost: bigint; costWithReferral: bigint; enabled: boolean }
/** The same numbers as /api/competitions/v4/config states them (synced from the chain). */
export type ApiFee = { creationWei?: string; withReferralWei?: string; enabled?: boolean }

const toBig = (v: unknown): bigint | undefined => {
  if (typeof v !== 'string' || !/^\d+$/.test(v)) return undefined
  return BigInt(v)
}
export function parseApiFee(f: ApiFee | null | undefined): ChainFee | undefined {
  const cost = toBig(f?.creationWei)
  const withRef = toBig(f?.withReferralWei)
  if (cost === undefined || typeof f?.enabled !== 'boolean') return undefined
  return { cost, costWithReferral: withRef ?? cost, enabled: f.enabled }
}

/**
 * The Launch step: what a CAD1 launch costs besides gas and the creator's own first buy (that buys the creator's tokens, it is not a fee):
 * the launcher's creationFee() plus the V3 lock's priceInNative(), both read live. "Free" only when both read 0; nothing while CAD1 is
 * not open (the step shows its "In testing" chip then) or when either read failed.
 */
export function launchBadge(i: { open: boolean; creationFee?: bigint; feeLoading: boolean; feeFailed: boolean; lock: LockFeeRead }): Badge {
  if (!i.open) return { kind: 'none' }
  if (i.feeLoading || i.lock.loading) return { kind: 'loading' }
  if (i.feeFailed || i.creationFee === undefined || i.lock.failed || i.lock.native === undefined) return { kind: 'none' }
  const total = i.creationFee + i.lock.native
  return total === 0n ? { kind: 'free' } : { kind: 'price', text: bnbText(total) }
}

export type CompetitionFeeInput = {
  /** a V4 contract is configured for this build */
  configured: boolean
  chain?: ChainFee
  chainLoading: boolean
  chainFailed: boolean
  api?: ApiFee | null
  apiLoading: boolean
}
export type FeeLine = { badge: Badge; sub: string | null; source: 'chain' | 'api' | null }

/** The Compete tile: the contract's own fee first, the API's copy of it when the chain read failed, never a number from nowhere. */
export function competitionFee(i: CompetitionFeeInput): FeeLine {
  if (!i.configured) return { badge: { kind: 'none' }, sub: null, source: null }
  const fromApi = i.chainFailed ? parseApiFee(i.api) : undefined
  const fee = i.chain ?? fromApi
  const source = i.chain ? 'chain' : fromApi ? 'api' : null
  if (!fee) {
    const waiting = i.chainLoading || (i.chainFailed && i.apiLoading)
    return { badge: waiting ? { kind: 'loading' } : { kind: 'none' }, sub: null, source: null }
  }
  if (!fee.enabled) return { badge: { kind: 'none' }, sub: null, source }
  if (fee.cost === 0n) return { badge: { kind: 'free' }, sub: null, source }
  const sub = fee.costWithReferral < fee.cost ? `${bnbText(fee.costWithReferral)} with a referral` : null
  return { badge: { kind: 'price', text: bnbText(fee.cost) }, sub, source }
}

/** A tick fee as the token-pages API states it (views/TokenPages/types Fee): 0 wei is free. */
export type TickFee = { wei?: string; bnb?: string; free?: boolean }
/** /api/token-pages/settings, the part the tile reads. `ticks` exists since free listing (2026-10-09); an older API has no such field. */
export type PagesSettings = { open?: boolean; fee?: { bnb?: string }; ticks?: { open?: boolean; blue?: TickFee; gold?: TickFee } | null }
export type PageLine = { badge: Badge; sub: string | null; sub2: string | null; subLoading: boolean }

/** "free", "0.01 BNB", or null when the API's answer is not a fee */
function tickText(f?: TickFee | null): string | null {
  if (!f || typeof f.wei !== 'string' || !/^\d+$/.test(f.wei)) return null
  if (isFree(f)) return 'free'
  return typeof f.bnb === 'string' && /^\d+(\.\d+)?$/.test(f.bnb) ? `${f.bnb} BNB` : null
}

/**
 * The Token page tile: every BNB Chain token has a page at no cost (it is built from the chain), so the badge is "FREE".
 * New API (`ticks` present, Bez 2026-10-09: listing is free, the ticks are the paid steps): the line under it is the blue team tick's fee
 * and, on a second line, the gold tick's, both as the API states them; nothing when tick requests are closed.
 * Old API (no `ticks`): the verified-info fee, only when submissions are open (the line this tile had first).
 */
export function tokenPageLine(s: PagesSettings | null | undefined, loading: boolean): PageLine {
  const free: Badge = { kind: 'free' }
  if (s?.ticks && typeof s.ticks === 'object') {
    if (s.ticks.open === false) return { badge: free, sub: null, sub2: null, subLoading: false }
    const blue = tickText(s.ticks.blue)
    const gold = tickText(s.ticks.gold)
    return { badge: free, sub: blue ? `Team tick ${blue}` : null, sub2: gold ? `Gold tick ${gold}` : null, subLoading: false }
  }
  const fee = s?.fee?.bnb
  const ok = typeof fee === 'string' && /^\d+(\.\d+)?$/.test(fee) && s?.open !== false
  if (!ok) return { badge: free, sub: null, sub2: null, subLoading: loading && !s }
  return { badge: free, sub: Number(fee) === 0 ? 'Verified info: free' : `Verified info: ${fee} BNB`, sub2: null, subLoading: false }
}

/** The Swap tile: how many DEXes the Smart Swap compares (the swap's own source list), or the plain router when the comparison is off. */
export function swapBadge(sources: readonly string[] | undefined, compareOn: boolean): Badge {
  const n = sources?.length ?? 0
  if (!compareOn || n < 2) return { kind: 'info', text: 'Smart Router' }
  return { kind: 'info', text: `${n} DEXes` }
}

/** "CADINUdex + 4 more": the swap's own source list, never typed by hand (short enough for a phone tile). */
export function sourcesLine(names: string[]): string | null {
  if (names.length === 0) return null
  if (names.length === 1) return names[0]
  return `${names[0]} + ${names.length - 1} more`
}

/** "3y", "13mo", "7d 21h", "19h 16m", "12m", "<1m": long lock dates in years or months, short ones to the minute. */
export function timeLeft(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  const d = Math.floor(s / 86400)
  if (d >= 730) return `${Math.floor(d / 365)}y`
  if (d >= 60) return `${Math.floor(d / 30)}mo`
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m`
  return '<1m'
}

export type BoardCompetition = CompetitionRow & { legacy?: boolean }
/**
 * The competitions on the home board: live ones first (ending soonest first), then upcoming ones (starting soonest first), from the
 * current contract and the previous one (tagged legacy). A row whose end has passed while the API still says "live" is left out.
 */
export function pickCompetitions(cur: CompetitionRow[] | undefined, old: CompetitionRow[] | undefined, nowSec: number, max = 3) {
  const rows: BoardCompetition[] = [...(cur ?? []), ...(old ?? []).map((r) => ({ ...r, legacy: true }))]
  const live = rows.filter((r) => r.phase === 'live' && r.endTime > nowSec).sort((a, b) => a.endTime - b.endTime)
  const upcoming = rows.filter((r) => r.phase === 'upcoming').sort((a, b) => a.startTime - b.startTime)
  return { rows: [...live, ...upcoming].slice(0, max), live: live.length, upcoming: upcoming.length }
}

/** "Hot on CADINUdex" for the home: the list's own hot order first, then the pinned tokens by 24 h volume, then liquidity. */
export function pickHotTokens(items: ListItem[] | undefined, max = 5): ListItem[] {
  if (!items?.length) return []
  const hot = items.filter((i) => i.hotRank !== null).sort((a, b) => (a.hotRank as number) - (b.hotRank as number))
  const rest = items
    .filter((i) => i.hotRank === null)
    .sort((a, b) => (b.volume24hUsd ?? -1) - (a.volume24hUsd ?? -1) || (b.liquidityUsd ?? -1) - (a.liquidityUsd ?? -1))
  return [...hot, ...rest].slice(0, max)
}

/** The lock board's line under a lock: what happens next and when. */
export function lockWhen(l: Pick<LockSummary, 'phase' | 'unlockAt' | 'nextAt'>, nowSec: number): string {
  const { phase }: { phase: LockPhase } = l
  if (phase === 'locked') return `Unlocks in ${timeLeft(l.unlockAt - nowSec)}`
  if (phase === 'vesting') return l.nextAt ? `Next release in ${timeLeft(l.nextAt - nowSec)}` : 'Vesting'
  if (phase === 'due') return 'Unlock date passed'
  return 'Released'
}

/** The lock board's heading facts, from the index totals; null when there is nothing to state. */
export function lockTotalsLine(t: Totals | null | undefined): string | null {
  if (!t || !Number.isFinite(t.active) || t.active <= 0) return null
  const n = `${t.active} active ${t.active === 1 ? 'lock' : 'locks'}`
  if (!Number.isFinite(t.lockedUsd) || t.lockedUsd <= 0) return n
  const usd = t.lockedUsd >= 1000 ? `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(t.lockedUsd)}` : `$${Math.round(t.lockedUsd)}`
  return `${n} · ${usd} locked`
}
